import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';

const logger = Logger.getLogger(Category.Multiplayer);

export interface ISocketListener
{
    onOpen?(event:Event):void;

    onClose?(event:CloseEvent):void;

    onReconnect?(event:Event):void;

    onError?(event:Event):void;

    onMessage?(event:MessageEvent):void;
}

export enum BinaryType
{
    ArrayBuffer = 'arraybuffer',
    Blob = 'blob'
}

export class Socket
{
    private _ws:WebSocket = null;
    private _url:string;
    private _listeners:ISocketListener[] = [];
    private _pendingMessages:any[] = [];

    private _binaryType:BinaryType = BinaryType.Blob;
    private _maxConnectionRetries:number = 5; // null means infinite number of times
    private _curConnectionRetry:number = 0;
    private _connectionRetryInterval:number = 1000; // ms
    private _connectionRetryTimer = null;
    private _manuallyClosed:boolean = false;

    constructor(url:string, listeners?:ISocketListener[])
    {
        if( !(window as any).WebSocket )
        {
            throw new Error(`Error: browser does not support WebSocket`);
        }

        this._url = url;
        this._listeners = listeners || [];

        this._tryConnect();
    }

    public sendMessage(message:any)
    {
        if( this.isOpen() )
        {
            this._ws.send(message);
        }
        else
        {
            this._pendingMessages.push(message);
        }
    }

    public isOpen():boolean
    {
        return this._ws && this._ws.readyState === 1;
    }

    public setBinaryType(binaryType:BinaryType):void
    {
        if( this._ws )
        {
            this._binaryType = binaryType;
            this._ws.binaryType = binaryType;
        }
    }

    public setMaxConnectionRetries(retries:number):void
    {
        this._maxConnectionRetries = retries;
    }

    public setConnectionRetryInterval(interval:number):void
    {
        this._connectionRetryInterval = interval;
    }

    public addListener(listener:ISocketListener):void
    {
        this._listeners.push(listener);
    }

    public removeListener(listener:ISocketListener):void
    {
        this._listeners = this._listeners.filter((item) => item !== listener);
    }

    public close():void
    {
        if( this._ws )
        {
            this._manuallyClosed = true;

            this._ws.close();
            this._ws = null;
            this._listeners = [];
        }
    }

    private _tryConnect():void
    {
        const ws = this._ws = new WebSocket(this._url);

        this.setBinaryType(this._binaryType);

        ws.addEventListener('open', (event:Event) =>
        {
            logger.debug(`WebSocket onopen:`, event);

            if( this._connectionRetryTimer !== null )
                clearTimeout(this._connectionRetryTimer);

            this._sendPendingMessages();

            this._listeners.forEach(({onOpen}) =>
            {
                if( onOpen )
                    onOpen(event);
            });

            if( this._curConnectionRetry !== 0 )
            {
                this._listeners.forEach(({onReconnect}) =>
                {
                    if( onReconnect )
                        onReconnect(event);
                });
            }

            this._curConnectionRetry = 0;
        });

        ws.addEventListener('close', (event:CloseEvent) =>
        {
            logger.debug(`WebSocket onclose:`, event);

            if( this._connectionRetryTimer !== null )
                clearTimeout(this._connectionRetryTimer);

            this._listeners.forEach(({onClose}) =>
            {
                if( onClose )
                    onClose(event);
            });

            if( !this._manuallyClosed )
            {
                if( this._maxConnectionRetries === null || this._curConnectionRetry < this._maxConnectionRetries )
                {
                    this._connectionRetryTimer = setTimeout(() =>
                    {
                        this._curConnectionRetry += 1;

                        this._tryConnect();
                    }, this._connectionRetryInterval);
                }
                else
                    logger.error(`WebSocket failed to connect to url ${this._url}`);
            }
        });

        ws.addEventListener('message', (event:MessageEvent) =>
        {
            // logger.debug(`WebSocket onmessage:`, event);

            if( event.data === 'ping' )
            {
                // logger.debug(`WebSocket got PING - answering with PONG: `, event);
                ws.send('pong');
            }
            else
            {
                this._listeners.forEach(({onMessage}) =>
                {
                    if( onMessage )
                        onMessage(event);
                });
            }
        });

        ws.addEventListener('error', (event:Event) =>
        {
            logger.error(`WebSocket onerror:`, event);

            this._listeners.forEach(({onError}) =>
            {
                if( onError )
                    onError(event);
            });
        });
    }

    private _sendPendingMessages():void
    {
        this._pendingMessages.forEach((message) => this._ws.send(message));
        this._pendingMessages = [];
    }
}
